/**
 * CONSAI Business App - AI Report Thank You Page
 */
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  CheckCircle2, FileText, ClipboardList, Bot, BarChart3, Gift, ArrowRight, Crown 
} from 'lucide-react';
import { trackPurchase } from '../utils/metaPixel';

const ThankYouReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get amount from URL params (default to 999 if not provided)
  const amountPaid = parseInt(searchParams.get('amount') || '999', 10);
  const displayAmount = amountPaid >= 100 ? amountPaid : amountPaid * 100; // Handle paise conversion if needed
  const formattedAmount = (displayAmount / 100).toLocaleString('en-IN');

  // Fire Meta Pixel event on page load
  useEffect(() => {
    trackPurchase('AI Business Report', displayAmount / 100);
  }, [displayAmount]);

  const steps = [
    { icon: ClipboardList, text: 'Complete the business questionnaire (10 minutes)', highlight: true },
    { icon: Bot, text: 'AI will analyze your answers and generate the report', highlight: false },
    { icon: BarChart3, text: 'Report will appear in your "Reports & Plan" tab', highlight: false },
    { icon: Gift, text: 'BONUS: Premium features unlocked FREE for 30 days!', highlight: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_consai-foundation/artifacts/0mnri4ba_CONSAI%20Logo_compressed.png" 
            alt="CONSAI" 
            className="h-10 w-10"
          />
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-600">
            Skip to Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-[#021945] font-manrope mb-4">
            Payment Successful! 🎉
          </h1>
          <p className="text-lg text-slate-600">
            Complete the questionnaire to generate your personalized AI Business Report.
          </p>
        </div>

        {/* Purchase Details Card */}
        <Card className="mb-8 border-2 border-green-100 bg-green-50/30">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 font-manrope mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Purchase Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-green-100">
                <span className="text-slate-600">Product</span>
                <span className="font-semibold text-slate-900">AI Business Report</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-green-100">
                <span className="text-slate-600">Amount Paid</span>
                <span className="font-semibold text-green-600">₹{formattedAmount}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-600">Status</span>
                <span className="font-semibold text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" />
                  Paid
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What's Next Card */}
        <Card className="mb-8 border-2 border-[#021945]/10">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 font-manrope mb-4">
              What's Next?
            </h2>
            <div className="space-y-4">
              {steps.map((step, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-start gap-4 p-4 rounded-lg ${
                    step.highlight ? 'bg-[#021945]/5 border border-[#021945]/10' : 'bg-slate-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    step.highlight ? 'bg-[#021945] text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {step.highlight && idx === 0 ? (
                      <span className="text-sm font-bold">1</span>
                    ) : (
                      <step.icon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`${step.highlight ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Premium Trial Badge */}
        <Card className="mb-8 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-center gap-3">
              <Crown className="h-5 w-5 text-purple-600" />
              <span className="text-purple-700 font-medium">
                Premium Trial: 30 days remaining
              </span>
            </div>
          </CardContent>
        </Card>

        {/* CTA Button */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/report')}
            className="bg-[#d7263d] hover:bg-[#d7263d]/90 text-white px-8 py-6 text-lg shadow-lg"
            data-testid="start-questionnaire-btn"
          >
            Start Questionnaire
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <p className="mt-4 text-sm text-slate-500">
            Takes only 10 minutes to complete
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-slate-500 text-sm">
        <p>© 2026 BUFFETISM IT VENTURES PVT LTD. All rights reserved</p>
        <p className="mt-2">Need help? Contact support@consai.in</p>
      </footer>
    </div>
  );
};

export default ThankYouReport;
